// src/components/app/proyectos/sensorica/ProjectTimeline.tsx
'use client';

import { Box, Container, Typography, useTheme, useMediaQuery } from '@mui/material';
import { Phase } from '@/data/proyectos/sensorica/sensoricaData';

interface ProjectTimelineProps {
  phases: Phase[];
}

export default function ProjectTimeline({ phases }: ProjectTimelineProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Box sx={{ py: 6, bgcolor: 'background.default' }}>
      <Container maxWidth="lg">
        <Box sx={{ position: 'relative' }}>
          {/* Línea vertical central */}
          {!isMobile && (
            <Box
              sx={{
                position: 'absolute',
                left: '50%',
                top: 0,
                bottom: 0,
                width: 4,
                bgcolor: 'divider',
                transform: 'translateX(-50%)',
              }}
            />
          )}

          {phases.map((phase, index) => {
            const isLeft = index % 2 === 0;
            
            return (
              <Box
                key={phase.id}
                sx={{
                  display: 'flex',
                  justifyContent: isMobile ? 'flex-start' : isLeft ? 'flex-end' : 'flex-start',
                  mb: 6,
                  position: 'relative',
                }}
              >
                {/* Círculo indicador */}
                {!isMobile && (
                  <Box
                    sx={{
                      position: 'absolute',
                      left: '50%',
                      top: 20,
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      bgcolor: phase.color,
                      transform: 'translateX(-50%)',
                      zIndex: 2,
                      border: '4px solid',
                      borderColor: 'background.paper',
                      boxShadow: 2,
                    }}
                  />
                )}

                {/* Contenido */}
                <Box
                  sx={{
                    width: isMobile ? '100%' : '45%',
                    pl: isMobile ? 3 : 0,
                    position: 'relative',
                  }}
                >
                  {/* Línea conectora en móvil */}
                  {isMobile && (
                    <Box
                      sx={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: 4,
                        bgcolor: phase.color,
                      }}
                    />
                  )}

                  <Box
                    sx={{
                      p: 3,
                      borderRadius: 2,
                      bgcolor: 'background.paper',
                      boxShadow: 2,
                      border: '2px solid',
                      borderColor: `${phase.color}30`,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: 4,
                        borderColor: phase.color,
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Typography variant="h3" sx={{ fontSize: '2rem' }}>
                        {phase.icon}
                      </Typography>
                      <Box>
                        <Typography
                          variant="overline"
                          sx={{
                            color: phase.color,
                            fontWeight: 700,
                            display: 'block',
                          }}
                        >
                          {phase.title}
                        </Typography>
                        <Typography variant="h6" fontWeight={600}>
                          {phase.subtitle}
                        </Typography>
                      </Box>
                    </Box>

                    <Typography variant="body2" color="text.secondary">
                      {phase.description}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Container>
    </Box>
  );
}